import { Alert } from "@mui/material";
import { customInput } from "modules/custom/calculator";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { PRODUCTS, PROPOSAL, SAVE_ITEM, SETTINGS } from "modules/custom/api";
import UiButton from "components/base/UiButton";
import MDBox from "components/MDBox";
import UiSideModal from "components/base/UiSideModal";
import UiTypography from "components/base/UiTypography";
import { Proposal, ProposalStage } from "generated/graphql";
import useCustomAddProductsToProposalStage from "hooks/proposals/useCustomAddProductsToProposalStage";
import SelectProductForm, { ProductMeasurement } from "modules/products/forms/SelectProductForm";
import { useCallback, useState, useEffect, useRef } from "react";

type AddProductDrawerProps = {
  open: boolean;
  onClose: () => void;
  stage: ProposalStage;
  onSuccess: (proposal: Proposal) => void;
  onAddBlankCustom?: () => void;
};

export default function AddProductDrawer({
  open,
  onClose,
  stage,
  onSuccess,
  onAddBlankCustom,
}: AddProductDrawerProps) {
  const client = useApolloClient();
  const inserted = useRef(new Set<string>());
  useEffect(() => {
    if (open) {
      inserted.current.clear();
      setFailure("");
    }
  }, [open]);
  const [saveCustom, { loading: savingCustom }] = useMutation(SAVE_ITEM);
  const [failure, setFailure] = useState("");
  const { data: customSettings } = useQuery(SETTINGS, { skip: !open, fetchPolicy: "network-only" });
  const [addProductsToProposalStage, { loading, isSuccess, getData }] =
    useCustomAddProductsToProposalStage();

  const handleOnSubmit = useCallback(
    async (measurements: ProductMeasurement[]) => {
      setFailure("");
      try {
        const customSelections = measurements.filter((m) => m.id.startsWith("custom:"));
        const productionSelections = measurements.filter((m) => !m.id.startsWith("custom:"));
        if (customSelections.length) {
          const { data } = await client.query({ query: PRODUCTS, fetchPolicy: "network-only" });
          for (const selected of customSelections) {
            if (inserted.current.has(selected.clientId)) continue;
            const product = data.customProducts.find((p: any) => `custom:${p.id}` === selected.id);
            if (!product)
              throw new Error("This Custom product is no longer available. Reload the library.");
            await saveCustom({
              variables: {
                stageId: stage.id,
                input: customInput({
                  ...product.definition,
                  quantity: String(selected.jobSize),
                  sourceProductId: product.id,
                  sourceProductRevision: product.revision,
                }),
              },
            });
            inserted.current.add(selected.clientId);
          }
          await client.refetchQueries({ include: [PROPOSAL, "customGetProposal"] });
        }
        if (!productionSelections.length) {
          onClose();
          return { success: true };
        }
        const result = await addProductsToProposalStage({
          variables: {
            input: {
              proposalStageId: stage.id,
              products: productionSelections.map((measurement) => ({
                id: measurement.id,
                jobSize: parseInt(measurement.jobSize.toString()) || 0,
                dailyUnitsComplete: measurement.dailyUnitsComplete || null,
                jobDuration: parseInt(measurement.jobDuration) || null,
                ...(measurement.mapData && { mapData: measurement.mapData }),
              })),
            },
          },
        });

        const success = isSuccess(result.data);
        const data = getData(result);

        if (success) {
          onSuccess(data as Proposal);
          onClose();
        }

        return { success, data };
      } catch (e) {
        setFailure((e as Error).message);
        throw e;
      }
    },
    [
      client,
      saveCustom,
      stage.id,
      addProductsToProposalStage,
      isSuccess,
      getData,
      onSuccess,
      onClose,
    ]
  );

  return (
    <UiSideModal open={open} onClose={onClose} title="Add Product" width="598px">
      <MDBox p="24px" display="flex" flexDirection="column" gap="16px">
        {onAddBlankCustom && customSettings?.customSettings.enabled && (
          <UiButton
            uiVariant="primary-green"
            onClick={onAddBlankCustom}
            disabled={loading || savingCustom}
          >
            Add Blank Product
          </UiButton>
        )}
        <UiTypography uiVariant="heading-sm">Add from Library</UiTypography>
        {failure && <Alert severity="error">{failure}</Alert>}
        <SelectProductForm
          includeCustom={!!customSettings?.customSettings.enabled}
          onSubmit={handleOnSubmit}
          loading={loading || savingCustom}
          mapData={stage.mapData}
          hideSelectionLabel
          useNewDesign
        />
      </MDBox>
    </UiSideModal>
  );
}
